'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @a11y-contract DataTable (TanStack Table v8 compound)
 *
 * ROLE:     table (static) | grid (virtual=true)
 * KEYBOARD:
 *   - Tab moves focus across sortable headers and row interactive elements.
 *   - Enter or Space on a focused sortable header toggles sort.
 *   - Shift+Click selects a range from last-clicked row.
 *   - Selection header: tristate checkbox (aria-label="Select all rows"); Space toggles.
 * ARIA:
 *   - aria-sort on each sortable header (ascending/descending/none).
 *   - aria-multiselectable="true" when selection enabled.
 *   - Sort-change live region: polite, announces "Sorted by {column}, {direction}".
 *   - Selection-change live region: polite, debounced 250ms trailing,
 *     announces "{n} of {total} rows selected".
 * FOCUS:
 *   - Sortable headers + row interactive elements carry
 *     scroll-margin-block-start: var(--sticky-header-height, 0).
 * PRM:    No internal motion; table renders are static.
 * FORCED-COLORS: Standard table borders.
 * RTL:    Column order respects dir attribute.
 * RSC:    CLIENT (sorting/filtering/selection state).
 * WAI-APG: Grid pattern when virtualized.
 * SUBSTRATE: @tanstack/react-table v8.x
 */
import { useState, useCallback, useMemo, Children, isValidElement, } from 'react';
import { useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, getPaginationRowModel, } from '@tanstack/react-table';
import { DataTableProvider } from './context';
import { Toolbar } from './Toolbar';
import { Header } from './Header';
import { Body } from './Body';
import { Pagination } from './Pagination';
import { Search } from './Search';
import { ColumnFilter } from './ColumnFilter';
import styles from './DataTable.module.css';
const STICKY_HEADER_VAR = '--dt-sticky-header-height';
// We use display-name sentinel tags to slot children correctly.
// Children with `_dtSlot === 'toolbar'` render outside the <table>.
// Children with `_dtSlot === 'pagination'` render after the scroll area.
// Header and Body are the natural thead/tbody—they render inside <table>.
const TOOLBAR_SLOT = 'toolbar';
const PAGINATION_SLOT = 'pagination';
function isSlot(child, slot) {
    return child.type?._dtSlot === slot;
}
export function DataTable({ data, columns, pagination: paginationOpts, selection: selectionOpts, sortable = false, virtual = false, children, className, style, }) {
    const [internalSorting, setInternalSorting] = useState([]);
    const [internalColumnFilters, setInternalColumnFilters] = useState([]);
    const [internalRowSelection, setInternalRowSelection] = useState({});
    const [internalPaginationState, setInternalPaginationState] = useState({
        pageIndex: paginationOpts?.pageIndex ?? 0,
        pageSize: paginationOpts?.pageSize ?? 20,
    });
    const rowSelection = selectionOpts?.value !== undefined
        ? selectionOpts.value
        : internalRowSelection;
    const onRowSelectionChange = useCallback((updaterOrValue) => {
        const next = typeof updaterOrValue === 'function'
            ? updaterOrValue(rowSelection)
            : updaterOrValue;
        setInternalRowSelection(next);
        selectionOpts?.onChange?.(next);
    }, [rowSelection, selectionOpts]);
    const table = useReactTable({
        data,
        columns,
        state: {
            sorting: internalSorting,
            columnFilters: internalColumnFilters,
            rowSelection,
            pagination: internalPaginationState,
        },
        onSortingChange: setInternalSorting,
        onColumnFiltersChange: setInternalColumnFilters,
        onRowSelectionChange,
        onPaginationChange: (updater) => {
            const next = typeof updater === 'function'
                ? updater(internalPaginationState)
                : updater;
            setInternalPaginationState(next);
            paginationOpts?.onPageChange?.(next.pageIndex);
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: sortable ? getSortedRowModel() : undefined,
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: paginationOpts ? getPaginationRowModel() : undefined,
        enableRowSelection: selectionOpts?.enabled ?? false,
    });
    const ctx = useMemo(() => ({
        // Cast to Table<unknown>: context consumers only call display/state methods
        // (getRowModel, getState, getHeaderGroups, etc.) — none require the concrete
        // TData type. useTypedTable<TData>() restores the precise type for callers
        // that do need it.
        table: table,
        totalRows: data.length,
        selectionEnabled: selectionOpts?.enabled ?? false,
        sortable,
        virtual,
        stickyHeaderVar: STICKY_HEADER_VAR,
    }), [table, data.length, selectionOpts?.enabled, sortable, virtual]);
    // Partition children into toolbar, table-body children, and pagination
    const toolbarChildren = [];
    const tableChildren = [];
    const paginationChildren = [];
    Children.forEach(children, (child) => {
        if (!isValidElement(child)) {
            tableChildren.push(child);
            return;
        }
        if (isSlot(child, TOOLBAR_SLOT)) {
            toolbarChildren.push(child);
        }
        else if (isSlot(child, PAGINATION_SLOT)) {
            paginationChildren.push(child);
        }
        else {
            tableChildren.push(child);
        }
    });
    const role = virtual ? 'grid' : 'table';
    const cls = [styles.wrapper, className].filter(Boolean).join(' ');
    return (_jsx(DataTableProvider, { value: ctx, children: _jsxs("div", { className: cls, style: { [STICKY_HEADER_VAR]: '0px', ...style }, children: [toolbarChildren.length > 0 && (_jsx("div", { className: styles.toolbarSlot, children: toolbarChildren })), _jsx("div", { className: styles.scrollArea, children: _jsx("table", { role: role, className: styles.table, "aria-multiselectable": selectionOpts?.enabled ? 'true' : undefined, children: tableChildren }) }), paginationChildren.length > 0 && (_jsx("div", { className: styles.paginationSlot, children: paginationChildren }))] }) }));
}
// Attach slot markers for child detection
Toolbar._dtSlot = TOOLBAR_SLOT;
Pagination._dtSlot = PAGINATION_SLOT;
// Compound members
DataTable.Toolbar = Toolbar;
DataTable.Search = Search;
DataTable.ColumnFilter = ColumnFilter;
DataTable.Header = Header;
DataTable.Body = Body;
DataTable.Pagination = Pagination;
//# sourceMappingURL=DataTable.js.map